/**
 * CRM Dashboard - Main dashboard for CRM portal
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// @mui/material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";

// Argon components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonBadge from "components/ArgonBadge";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Services
import { contactService } from "services/firestoreService";
import { useAuth } from "contexts/AuthContext";

function CRMDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { hasPortalAccess, user } = useAuth();

  useEffect(() => {
    // Check portal access
    if (!hasPortalAccess('crm')) {
      navigate('/dashboard');
      return;
    }
    
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const contacts = await contactService.getAll(user.uid);
      // Derive dashboard data from contacts
      const newLeads = contacts.filter(c => c.lead_status?.toLowerCase() === 'new');
      const convertedLeads = contacts.filter(c => c.lead_status?.toLowerCase() === 'converted');
      const scores = contacts.map(c => c.lead_score || 0);
      const avgScore = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0;
      const conversionRate = contacts.length > 0 ? Math.round((convertedLeads.length / contacts.length) * 100) : 0;

      setDashboardData({
        lead_stats: {
          total_leads: contacts.length,
          new_leads: newLeads.length,
          conversion_rate: conversionRate,
          average_score: avgScore,
        },
        recent_leads: contacts.slice(0, 10),
        top_performing_leads: [...contacts].sort((a, b) => (b.lead_score || 0) - (a.lead_score || 0)).slice(0, 10),
      });
    } catch (error) {
      console.error('Failed to fetch CRM dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'new': return 'info';
      case 'contacted': return 'warning';
      case 'qualified': return 'success';
      case 'converted': return 'primary';
      case 'lost': return 'error';
      default: return 'secondary';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    if (score >= 40) return 'info';
    return 'error';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox py={3}>
          <ArgonBox display="flex" justifyContent="center" p={3}>
            <ArgonTypography variant="h6">Loading CRM Dashboard...</ArgonTypography>
          </ArgonBox>
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        {/* Header */}
        <ArgonBox mb={3}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <ArgonBox>
                <ArgonTypography variant="h4" fontWeight="medium">
                  CRM Dashboard
                </ArgonTypography>
                <ArgonTypography variant="body2" color="text">
                  Manage your leads, contacts, and sales pipeline
                </ArgonTypography>
              </ArgonBox>
            </Grid>
            <Grid item xs={12} md={6}>
              <ArgonBox display="flex" justifyContent="flex-end" gap={1}>
                <ArgonButton 
                  color="success" 
                  size="small"
                  onClick={() => navigate('/crm/dashboard')}
                >
                  <Icon>add</Icon>&nbsp;Add Lead
                </ArgonButton>
                <ArgonButton
                  color="info"
                  size="small"
                  onClick={() => navigate('/crm/dashboard')}
                >
                  <Icon>upload</Icon>&nbsp;Import Leads
                </ArgonButton>
              </ArgonBox>
            </Grid>
          </Grid>
        </ArgonBox>

        {/* Stats Cards */}
        <ArgonBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <ArgonBox display="flex" alignItems="center">
                    <ArgonBox
                      bgColor="info"
                      color="white"
                      width="3rem"
                      height="3rem"
                      borderRadius="md"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      mr={2}
                    >
                      <Icon fontSize="medium">people</Icon>
                    </ArgonBox>
                    <ArgonBox>
                      <ArgonTypography variant="h5" fontWeight="bold">
                        {dashboardData?.lead_stats?.total_leads || 0}
                      </ArgonTypography>
                      <ArgonTypography variant="caption" color="text">
                        Total Leads
                      </ArgonTypography>
                    </ArgonBox>
                  </ArgonBox>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <ArgonBox display="flex" alignItems="center">
                    <ArgonBox
                      bgColor="warning"
                      color="white"
                      width="3rem"
                      height="3rem"
                      borderRadius="md"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      mr={2}
                    >
                      <Icon fontSize="medium">fiber_new</Icon>
                    </ArgonBox>
                    <ArgonBox>
                      <ArgonTypography variant="h5" fontWeight="bold">
                        {dashboardData?.lead_stats?.new_leads || 0}
                      </ArgonTypography>
                      <ArgonTypography variant="caption" color="text">
                        New Leads
                      </ArgonTypography>
                    </ArgonBox>
                  </ArgonBox>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <ArgonBox display="flex" alignItems="center">
                    <ArgonBox
                      bgColor="success"
                      color="white"
                      width="3rem"
                      height="3rem"
                      borderRadius="md"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      mr={2}
                    >
                      <Icon fontSize="medium">trending_up</Icon>
                    </ArgonBox>
                    <ArgonBox>
                      <ArgonTypography variant="h5" fontWeight="bold">
                        {dashboardData?.lead_stats?.conversion_rate || 0}%
                      </ArgonTypography>
                      <ArgonTypography variant="caption" color="text">
                        Conversion Rate
                      </ArgonTypography>
                    </ArgonBox>
                  </ArgonBox>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <ArgonBox display="flex" alignItems="center">
                    <ArgonBox
                      bgColor="primary"
                      color="white"
                      width="3rem"
                      height="3rem"
                      borderRadius="md"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      mr={2}
                    >
                      <Icon fontSize="medium">psychology</Icon>
                    </ArgonBox>
                    <ArgonBox>
                      <ArgonTypography variant="h5" fontWeight="bold">
                        {dashboardData?.lead_stats?.average_score || 0}
                      </ArgonTypography>
                      <ArgonTypography variant="caption" color="text">
                        Avg. Score
                      </ArgonTypography>
                    </ArgonBox>
                  </ArgonBox>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </ArgonBox>

        {/* Tables */}
        <Grid container spacing={3}>
          {/* Recent Leads */}
          <Grid item xs={12} lg={6}>
            <Card>
              <ArgonBox p={3} display="flex" justifyContent="space-between" alignItems="center">
                <ArgonTypography variant="h6" fontWeight="medium">
                  Recent Leads
                </ArgonTypography>
                <ArgonButton 
                  color="info" 
                  size="small" 
                  variant="outlined"
                  onClick={() => navigate('/crm/dashboard')}
                >
                  View All
                </ArgonButton>
              </ArgonBox>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Score</TableCell>
                      <TableCell>Source</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData?.recent_leads?.slice(0, 5).map((lead) => (
                      <TableRow 
                        key={lead.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate('/crm/dashboard')}
                      >
                        <TableCell>
                          <ArgonBox>
                            <ArgonTypography variant="body2" fontWeight="medium">
                              {lead.first_name} {lead.last_name}
                            </ArgonTypography>
                            {lead.company && (
                              <ArgonTypography variant="caption" color="text">
                                {lead.company}
                              </ArgonTypography>
                            )}
                          </ArgonBox>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={lead.lead_status} 
                            color={getStatusColor(lead.lead_status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <ArgonBadge 
                            variant="contained"
                            color={getScoreColor(lead.lead_score)}
                            size="sm"
                            badgeContent={lead.lead_score}
                          />
                        </TableCell>
                        <TableCell>
                          <ArgonTypography variant="caption">
                            {lead.lead_source || 'Unknown'}
                          </ArgonTypography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>

          {/* Top Performing Leads */}
          <Grid item xs={12} lg={6}>
            <Card>
              <ArgonBox p={3} display="flex" justifyContent="space-between" alignItems="center">
                <ArgonTypography variant="h6" fontWeight="medium">
                  High-Score Leads
                </ArgonTypography>
                <ArgonButton 
                  color="success" 
                  size="small" 
                  variant="outlined"
                  onClick={() => navigate('/crm/dashboard')}
                >
                  View All
                </ArgonButton>
              </ArgonBox>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell align="center">Score</TableCell>
                      <TableCell>Budget</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData?.top_performing_leads?.slice(0, 5).map((lead) => (
                      <TableRow key={lead.id} hover>
                        <TableCell>
                          <ArgonBox>
                            <ArgonTypography variant="body2" fontWeight="medium">
                              {lead.first_name} {lead.last_name}
                            </ArgonTypography>
                            {lead.email && (
                              <ArgonTypography variant="caption" color="text">
                                {lead.email}
                              </ArgonTypography>
                            )}
                          </ArgonBox>
                        </TableCell>
                        <TableCell align="center">
                          <ArgonBadge 
                            variant="contained"
                            color="success"
                            size="sm"
                            badgeContent={lead.lead_score}
                          />
                        </TableCell>
                        <TableCell>
                          <ArgonTypography variant="caption">
                            {lead.budget_min ? formatCurrency(lead.budget_min) : 'N/A'}
                          </ArgonTypography>
                        </TableCell>
                        <TableCell>
                          <ArgonButton 
                            color="primary"
                            size="small"
                            onClick={() => navigate('/crm/dashboard')}
                          >
                            Contact
                          </ArgonButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>

        {/* AI Insights Card */}
        <ArgonBox mt={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox display="flex" alignItems="center" mb={2}>
                <Icon color="primary" sx={{ mr: 1 }}>psychology</Icon>
                <ArgonTypography variant="h6" fontWeight="medium">
                  AI Insights
                </ArgonTypography>
              </ArgonBox>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <ArgonBox p={2} bgcolor="grey.100" borderRadius="lg">
                    <ArgonTypography variant="caption" color="text" fontWeight="bold">
                      RECOMMENDED ACTION
                    </ArgonTypography>
                    <ArgonTypography variant="body2" mt={1}>
                      Focus on {dashboardData?.lead_stats?.new_leads || 0} new leads. 
                      Average response time impacts conversion by 25%.
                    </ArgonTypography>
                  </ArgonBox>
                </Grid>
                <Grid item xs={12} md={4}>
                  <ArgonBox p={2} bgcolor="info.lighter" borderRadius="lg">
                    <ArgonTypography variant="caption" color="text" fontWeight="bold">
                      TRENDING UP
                    </ArgonTypography>
                    <ArgonTypography variant="body2" mt={1}>
                      Website leads have 2x higher conversion rate than cold calls.
                    </ArgonTypography>
                  </ArgonBox>
                </Grid>
                <Grid item xs={12} md={4}>
                  <ArgonBox p={2} bgcolor="success.lighter" borderRadius="lg">
                    <ArgonTypography variant="caption" color="text" fontWeight="bold">
                      OPTIMIZATION TIP
                    </ArgonTypography>
                    <ArgonTypography variant="body2" mt={1}>
                      Leads with complete profiles score 40% higher on average.
                    </ArgonTypography>
                  </ArgonBox>
                </Grid>
              </Grid>
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CRMDashboard;